import { useQuery, useMutation } from "@tanstack/react-query";
import {
  UserAppealsOrderingField,
  AppealFileDeleteRequest,
  AppealRepliesOrderingField,
} from "../types/api/appeals";
import {
  fetchUserAppealsList,
  fetchAppealTopics,
  deleteAppealFile,
  fetchAppealReplies,
} from "../services/appeals";
import { FeedbackStatus } from "../types/api/feedback";

export const useUserAppealsQuery = (
  search: string = "",
  page: number,
  limit: number,
  ordering?: UserAppealsOrderingField,
  topicId?: string | undefined,
  status?: FeedbackStatus
) => {
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: ["user-appeals", search, page, limit, ordering, topicId, status],
    queryFn: () =>
      fetchUserAppealsList({
        search: search || undefined,
        limit,
        offset: (page - 1) * limit,
        ordering,
        topic_id: topicId,
        status: status,
      }),
    retry: 0,
  });
};

export const useAppealTopicsQuery = (page: number, limit: number = 10) => {
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: ["appeal-topics", page, limit],
    queryFn: () =>
      fetchAppealTopics({
        limit,
        offset: (page - 1) * limit,
      }),
    retry: 0,
  });
};

export const useDeleteAppealFileMutation = (onSuccess?: () => void) => {
  return useMutation({
    mutationFn: ({
      appealId,
      data,
    }: {
      appealId: string;
      data: AppealFileDeleteRequest;
    }) => deleteAppealFile(appealId, data),
    onSuccess: () => {
      onSuccess?.();
    },
  });
};

export const useAppealRepliesQuery = (
  appealId: string,
  search: string = "",
  page: number = 1,
  limit: number = 10,
  ordering?: AppealRepliesOrderingField[]
) => {
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: ["appeal-replies", appealId, search, page, limit, ordering],
    queryFn: () =>
      fetchAppealReplies(appealId, {
        search: search || undefined,
        limit,
        offset: (page - 1) * limit,
        ordering,
      }),
    retry: 0,
  });
};
